import { Alert, Box, MenuItem, TextField } from '@mui/material';
import { useState } from 'react';

import { adjustStock, issueStock, receiveStock, writeOffStock } from '../../api/business/endpoints';
import type { ProductDetail, StockOperation } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { FormDialog } from '../../components/FormDialog';
import { formatNumber } from '../../components/format';
import { parseMoneyInput, parseWholeNumber } from '../../components/parse';
import { batchLabel } from '../../components/batchLabel';
import { packIssueLines } from '../../components/packItems';
import { isVariable, receivingVariants, variantLabel } from '../../components/variants';
import { useLanguage } from '../../i18n/LanguageContext';

import type { StockOperationKind } from './StockOperationKind';

const NEW_BATCH = '__new__';

const AUTOMATIC = '__fefo__';

const TITLES = {
  receipt: 'stock.receipt',
  issue: 'stock.issue',
  adjustment: 'stock.adjustment',
  writeOff: 'stock.writeOff',
} as const;

type StockOperationDialogProps = {
  kind: StockOperationKind;
  companyId: string;
  currency: string;
  product: ProductDetail;
  presetBatchId?: string;
  onClose: () => void;
  onDone: (operation: StockOperation) => void;
};

/**
 * Receipt, issue, adjustment or write-off — every quantity change is one of these (BR-16). Each opening of
 * the dialog carries one idempotency key, so a double click or a retried request records the operation once
 * (AC-09.5). A pack (R0030 WP-09) is only issued: no batch or variant to choose — the dialog shows what each item
 * gives, all of it or nothing.
 */
export const StockOperationDialog = ({
  kind,
  companyId,
  currency,
  product,
  presetBatchId,
  onClose,
  onDone,
}: StockOperationDialogProps) => {
  const { language, t } = useLanguage();
  const [idempotencyKey] = useState<string>(() => crypto.randomUUID());
  const defaultBatch = kind === 'receipt' ? NEW_BATCH : kind === 'issue' ? AUTOMATIC : '';
  // A receipt defaults to a new batch and an issue to FEFO; only adjustments and write-offs, which must name a
  // batch, fall back to the first one. Letting the first batch win for every kind made "Výdaj" skip FEFO.
  const [batch, setBatch] = useState<string>(
    presetBatchId ?? (defaultBatch !== '' ? defaultBatch : (product.batches[0]?.id ?? '')),
  );
  // A variable product's operation names its variant (ADR-0010 rule 10): picked here, or the preset batch's.
  const variable = isVariable(product) && product.type !== 'PACK';
  const variantChoices = kind === 'receipt' ? receivingVariants(product) : product.variants;
  const [variantId, setVariantId] = useState<string>(
    () =>
      product.batches.find((one) => one.id === presetBatchId)?.variantId ??
      (variantChoices.length === 1 ? variantChoices[0].id : ''),
  );
  const [expiryDate, setExpiryDate] = useState('');
  const [lotNumber, setLotNumber] = useState('');
  const [quantity, setQuantity] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const selected = product.batches.find((one) => one.id === batch) ?? null;
  const pack = product.type === 'PACK';
  const optional = (text: string): string | undefined => text.trim() || undefined;

  const mutation = useAppMutation(
    (amount: number): Promise<StockOperation> => {
      const common = {
        productId: product.id,
        variantId: variable && variantId !== '' ? variantId : undefined,
        note: optional(note),
        idempotencyKey,
      };

      switch (kind) {
        case 'receipt':
          return receiveStock(companyId, {
            ...common,
            batchId: batch === NEW_BATCH ? undefined : batch,
            expiryDate: batch === NEW_BATCH ? expiryDate || undefined : undefined,
            lotNumber: batch === NEW_BATCH ? optional(lotNumber) : undefined,
            quantity: amount,
            purchasePriceMinor: parseMoneyInput(purchasePrice) ?? undefined,
            reference: optional(reference),
          });
        case 'issue':
          return issueStock(companyId, {
            ...common,
            batchId: batch === AUTOMATIC || product.type === 'PACK' ? undefined : batch,
            quantity: amount,
            reference: optional(reference),
          });
        case 'adjustment':
          return adjustStock(companyId, { ...common, batchId: batch, countedQuantity: amount });
        case 'writeOff':
          return writeOffStock(companyId, { ...common, batchId: batch, quantity: amount });
      }
    },
    ['product', 'products', 'movements', 'licence-usage'],
    onDone,
  );

  const submit = (): void => {
    const amount = parseWholeNumber(quantity);

    if (amount === undefined || (kind !== 'adjustment' && amount === 0)) {
      setProblem(t('stock.quantityInvalid'));

      return;
    }

    if (kind === 'receipt' && parseMoneyInput(purchasePrice) === undefined) {
      setProblem(t('money.invalid'));

      return;
    }

    if (variable && variantId === '' && (batch === NEW_BATCH || batch === AUTOMATIC)) {
      setProblem(t('stock.variantRequired'));

      return;
    }

    if ((kind === 'adjustment' || kind === 'writeOff') && selected === null) {
      setProblem(t('stock.batchRequired'));

      return;
    }

    setProblem(null);
    mutation.mutate(amount);
  };

  const receiving = new Set(variantChoices.map((one) => one.id));
  const batchOptions = product.batches
    .filter((one) =>
      !variable
        ? true
        : variantId === ''
          ? kind !== 'receipt' || receiving.has(one.variantId)
          : one.variantId === variantId,
    )
    .map((one) => (
      <MenuItem key={one.id} value={one.id}>
        {batchLabel(one, t, language)} —{' '}
        {t('stock.onHandShort', { count: formatNumber(one.onHand, language) })}
      </MenuItem>
    ));

  return (
    <FormDialog
      title={`${t(TITLES[kind])} — ${product.name}`}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t(TITLES[kind])}
      isBusy={mutation.isPending}
      error={mutation.error}
      problem={problem}
    >
      {pack ? (
        <Alert severity="info" variant="outlined">
          {t('stock.packIssueHint', { count: formatNumber(parseWholeNumber(quantity) ?? 0, language) })}
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {packIssueLines(product.packItems, parseWholeNumber(quantity) ?? 0).map((line) => (
              <li key={line.name}>
                {t('stock.packIssueLine', { name: line.name, count: formatNumber(line.count, language) })}
              </li>
            ))}
          </Box>
        </Alert>
      ) : null}

      {variable ? (
        <TextField
          select
          label={t('field.variant')}
          value={variantId}
          onChange={(e) => {
            setVariantId(e.target.value);

            if (product.batches.find((one) => one.id === batch)?.variantId !== e.target.value) {
              setBatch(defaultBatch);
            }
          }}
        >
          {variantChoices.map((one) => (
            <MenuItem key={one.id} value={one.id}>
              {variantLabel(one, t)}
            </MenuItem>
          ))}
        </TextField>
      ) : null}

      {pack ? null : (
        <TextField
          select
          label={t('field.batch')}
          value={batch}
          onChange={(e) => {
            setBatch(e.target.value);
            setVariantId(product.batches.find((one) => one.id === e.target.value)?.variantId ?? variantId);
          }}
        >
          {kind === 'receipt' ? <MenuItem value={NEW_BATCH}>{t('stock.newBatch')}</MenuItem> : null}
          {kind === 'issue' ? <MenuItem value={AUTOMATIC}>{t('stock.fefo')}</MenuItem> : null}
          {batchOptions}
        </TextField>
      )}

      {kind === 'issue' && batch === AUTOMATIC && !pack ? (
        <Alert severity="info" variant="outlined">
          {t('stock.fefoHint')}
        </Alert>
      ) : null}

      {kind === 'receipt' && batch === NEW_BATCH ? (
        <>
          <TextField
            type="date"
            label={t('field.expiryDate')}
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={t('field.lotNumber')}
            value={lotNumber}
            onChange={(e) => setLotNumber(e.target.value)}
          />
        </>
      ) : null}

      <TextField
        required
        autoFocus
        label={kind === 'adjustment' ? t('stock.counted') : t('field.quantity')}
        helperText={
          kind === 'adjustment' && selected
            ? t('stock.countedHint', { count: formatNumber(selected.onHand, language) })
            : t('stock.piecesHint')
        }
        value={quantity}
        inputProps={{ inputMode: 'numeric' }}
        onChange={(e) => setQuantity(e.target.value)}
      />

      {kind === 'receipt' ? (
        <TextField
          label={t('field.purchasePrice', { currency })}
          value={purchasePrice}
          inputProps={{ inputMode: 'decimal' }}
          onChange={(e) => setPurchasePrice(e.target.value)}
        />
      ) : null}
      {kind === 'receipt' || kind === 'issue' ? (
        <TextField
          label={t('field.reference')}
          helperText={t('stock.referenceHint')}
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />
      ) : null}
      <TextField
        multiline
        minRows={2}
        label={t('field.note')}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
    </FormDialog>
  );
};
